import Transaction from './Transaction';
import Stock from './Stock';
import {
  formatAnalystRating,
  getAnalystRatingBadgeClass,
  getChartRatingBucket,
  CHART_RATING_COLORS,
  CHART_RATING_LABELS,
  CHART_RATING_ORDER
} from '../AnalystRating';

class Portfolio {
  chartData = null;

  benchmarkData = null;

  get transactions() {
    return Transaction.findAll() || [];
  }

  getPortfolioValue() {
    const fromStocks = this._getPortfolioValueForStocksMap(this._getStocksMap());
    if (Number.isFinite(fromStocks) && fromStocks > 0) {
      return fromStocks;
    }

    const lastChartValue = this._getLastChartValue();
    if (lastChartValue != null) {
      return lastChartValue;
    }

    return Number.isFinite(fromStocks) ? fromStocks : 0;
  }

  getOneYearForecast() {
    const stocks = this._getStocksMap();
    const tickers = Object.keys(stocks);
    if (tickers.length < 1) {
      return null;
    }

    let forecastValue = 0;
    let hasPriceData = false;

    tickers.forEach((ticker) => {
      const { totalShares, targetPrice, lastPrice } = stocks[ticker];
      if (typeof targetPrice === 'number') {
        forecastValue += targetPrice * totalShares;
        hasPriceData = true;
      } else if (typeof lastPrice === 'number') {
        forecastValue += lastPrice * totalShares;
        hasPriceData = true;
      }
    });

    return hasPriceData ? forecastValue : null;
  }

  getDayChange() {
    const stocks = this._getStocksMap();
    const tickers = Object.keys(stocks);
    if (tickers.length < 1) {
      return null;
    }

    let dollarChange = 0;
    let hasRegularMarketChange = false;

    tickers.forEach((ticker) => {
      const stock = Stock.find(ticker);
      const shares = stocks[ticker].totalShares;
      if (stock && typeof stock.regularMarketChange === 'number') {
        dollarChange += stock.regularMarketChange * shares;
        hasRegularMarketChange = true;
      }
    });

    if (hasRegularMarketChange) {
      const portfolioValue = this.getPortfolioValue();
      const previousValue = portfolioValue - dollarChange;
      if (!previousValue) {
        return null;
      }
      return {
        dollarChange,
        percentChange: (dollarChange / previousValue) * 100
      };
    }

    if (!this.chartData?.values || this.chartData.values.length < 2) {
      return null;
    }

    const lastTwoValues = this._getLastTwoChartValues();
    if (!lastTwoValues) {
      return null;
    }

    const { current, previous } = lastTwoValues;
    if (!previous) {
      return null;
    }

    const chartDollarChange = current - previous;
    const chartPercentChange = (chartDollarChange / previous) * 100;
    if (!Number.isFinite(chartDollarChange) || !Number.isFinite(chartPercentChange)) {
      return null;
    }

    return {
      dollarChange: chartDollarChange,
      percentChange: chartPercentChange
    };
  }

  setChartData(chartData) {
    this.chartData = chartData;
  }

  setBenchmarkData(benchmarkData) {
    this.benchmarkData = benchmarkData;
  }

  getHoldingsForChart() {
    return this.transactions.map((txn) => {
      const parsedDate = new Date(txn.date);
      return {
        ticker: txn.stockTicker,
        quantity: txn.numberOfShares,
        date: Number.isNaN(parsedDate.getTime()) ? txn.date : parsedDate.toISOString()
      };
    });
  }

  getChartData() {
    if (!this.chartData || !this.chartData.dates || this.chartData.dates.length < 1) {
      return { labels: [], series: [[]] };
    }

    return {
      labels: this.chartData.dates.map((dateStr) => {
        const date = new Date(`${dateStr}T00:00:00`);
        return `${date.getMonth() + 1}/${date.getDate()}`;
      }),
      series: [this.chartData.values]
    };
  }

  getTopSectors() {
    const stocks = this._getStocksMap();
    const tickers = Object.keys(stocks);
    if (tickers.length < 1) {
      return { labels: [], series: [] };
    }

    const sectorValues = {};
    tickers.forEach((ticker) => {
      const stock = Stock.find(ticker);
      const sector = stock && stock.sector ? stock.sector : 'Unknown';
      if (sector.toLowerCase() === 'etf') {
        return;
      }
      const holdingValue = stocks[ticker].currentValue;
      sectorValues[sector] = (sectorValues[sector] || 0) + holdingValue;
    });

    const sortedSectors = Object.entries(sectorValues)
      .sort((a, b) => b[1] - a[1]);

    const topSectors = sortedSectors.slice(0, 5);
    const othersValue = sortedSectors
      .slice(5)
      .reduce((sum, [, value]) => sum + value, 0);

    const labels = topSectors.map(([sector]) => sector);
    const series = topSectors.map(([, value]) => value);

    if (othersValue > 0) {
      labels.push('Others');
      series.push(othersValue);
    }

    const totalValue = Object.values(sectorValues).reduce((sum, value) => sum + value, 0);

    return {
      labels,
      series,
      totalValue
    };
  }

  getCountryExposure() {
    const stocks = this._getStocksMap();
    const tickers = Object.keys(stocks);
    if (tickers.length < 1) {
      return [];
    }

    const countries = new Set();
    tickers.forEach((ticker) => {
      const stock = Stock.find(ticker);
      if (stock && stock.country) {
        countries.add(stock.country);
      }
    });

    return Array.from(countries);
  }

  getTopHoldings() {
    const { transactions } = this;
    if (transactions.length < 1) return [];

    const stocks = this._getStocksMap();
    const portfolioValue = this._getPortfolioValueForStocksMap(stocks);
    const topStocks = Object.keys(stocks).sort((a, b) => {
      return stocks[a].currentValue - stocks[b].currentValue;
    });

    const topHoldings = [];
    let idx = 5;
    while (topStocks.length > 0 && idx > 0) {
      const ticker = topStocks.pop();
      const holdingValue = stocks[ticker].currentValue;
      const stock = Stock.find(ticker);
      const recommendationKey = stock && stock.recommendationKey ? stock.recommendationKey : null;
      topHoldings.push({
        text: ticker,
        percent: ((holdingValue / portfolioValue) * 100).toFixed(2),
        recommendation: formatAnalystRating(recommendationKey),
        recommendationBadgeClass: getAnalystRatingBadgeClass(recommendationKey)
      });
      idx--;
    }

    return topHoldings;
  }

  getTopGainers() {
    return this._getTopMovers('gain');
  }

  getTopLosers() {
    return this._getTopMovers('loss');
  }

  _getTopMovers(direction) {
    const isGain = direction === 'gain';
    const holdings = this.getHoldings().items;

    const movers = holdings
      .filter(({ change }) => isGain ? change > 0 : change < 0)
      .sort((a, b) => isGain ? b.change - a.change : a.change - b.change)
      .slice(0, 5);

    return {
      columns: ['Stock', isGain ? 'Gain' : 'Loss'],
      items: movers.map(({ ticker, change }) => {
        const stock = Stock.find(ticker);
        const recommendationKey = stock && stock.recommendationKey ? stock.recommendationKey : null;
        return {
          text: ticker,
          percent: change,
          recommendation: formatAnalystRating(recommendationKey),
          recommendationBadgeClass: getAnalystRatingBadgeClass(recommendationKey)
        };
      })
    };
  }

  getAssetAllocation() {
    const assetClasses = ['Equities', 'Fixed Income', 'Cash'];
    const stocks = this._getStocksMap();
    const tickers = Object.keys(stocks);
    if (tickers.length < 1) {
      return { labels: [], series: [] };
    }

    const allocationValues = {
      Equities: 0,
      'Fixed Income': 0,
      Cash: 0
    };

    tickers.forEach((ticker) => {
      const stock = Stock.find(ticker);
      const assetClass = this._getAssetClass(ticker, stock);
      allocationValues[assetClass] += stocks[ticker].currentValue;
    });

    const totalValue = Object.values(allocationValues).reduce((sum, value) => sum + value, 0);
    const labels = assetClasses.filter((assetClass) => allocationValues[assetClass] > 0);
    const series = labels.map((assetClass) => allocationValues[assetClass]);

    return {
      labels,
      series,
      totalValue
    };
  }

  getAnalystRatings() {
    const stocks = this._getStocksMap();
    const tickers = Object.keys(stocks);
    if (tickers.length < 1) {
      return { items: [], labels: [], series: [], totalCount: 0, colors: [] };
    }

    const ratingCounts = {
      strong_buy: 0,
      buy: 0,
      hold: 0,
      sell: 0
    };
    const items = [];

    tickers.forEach((ticker) => {
      const stock = Stock.find(ticker);
      const recommendationKey = stock && stock.recommendationKey ? stock.recommendationKey : null;
      const bucket = getChartRatingBucket(recommendationKey);
      if (bucket) {
        ratingCounts[bucket] += 1;
        items.push({
          ticker,
          bucket,
          color: CHART_RATING_COLORS[bucket]
        });
      }
    });

    items.sort((a, b) => {
      const orderDiff = CHART_RATING_ORDER.indexOf(a.bucket) - CHART_RATING_ORDER.indexOf(b.bucket);
      if (orderDiff !== 0) {
        return orderDiff;
      }
      return a.ticker.localeCompare(b.ticker);
    });

    const labels = [];
    const series = [];
    const colors = [];

    CHART_RATING_ORDER.forEach((bucket) => {
      const count = ratingCounts[bucket];
      if (count > 0) {
        labels.push(CHART_RATING_LABELS[bucket]);
        series.push(count);
        colors.push(CHART_RATING_COLORS[bucket]);
      }
    });

    const totalCount = series.reduce((sum, count) => sum + count, 0);

    return {
      items,
      labels,
      series,
      totalCount,
      colors
    };
  }

  getBeta() {
    const stocks = this._getStocksMap();
    const tickers = Object.keys(stocks);
    if (tickers.length < 1) {
      return null;
    }

    const portfolioValue = this._getPortfolioValueForStocksMap(stocks);
    if (!portfolioValue) {
      return null;
    }

    const weightedBeta = tickers.reduce((sum, ticker) => {
      const stock = Stock.find(ticker);
      const beta = stock && typeof stock.beta === 'number' ? stock.beta : 1;
      const weight = stocks[ticker].currentValue / portfolioValue;
      return sum + (beta * weight);
    }, 0);

    return Math.round(weightedBeta * 100) / 100;
  }

  getAnnualDividend() {
    const stocks = this._getStocksMap();
    const tickers = Object.keys(stocks);
    if (tickers.length < 1) {
      return null;
    }

    const portfolioValue = this._getPortfolioValueForStocksMap(stocks);
    if (!portfolioValue) {
      return null;
    }

    let annualIncome = 0;
    let hasDividendData = false;

    tickers.forEach((ticker) => {
      const stock = Stock.find(ticker);
      const { totalShares, lastPrice } = stocks[ticker];
      if (
        stock &&
        typeof stock.dividendYield === 'number' &&
        typeof lastPrice === 'number'
      ) {
        annualIncome += stock.dividendYield * lastPrice * totalShares;
        hasDividendData = true;
      }
    });

    if (!hasDividendData) {
      return null;
    }

    return {
      amount: annualIncome,
      percentOfPortfolio: (annualIncome / portfolioValue) * 100
    };
  }

  getPeriodReturns() {
    const alignedReturns = this._getAlignedPeriodReturns();
    if (!alignedReturns) {
      return null;
    }

    const { portfolioReturn, marketReturn } = alignedReturns;
    return {
      portfolioReturnPct: portfolioReturn * 100,
      marketReturnPct: marketReturn * 100
    };
  }

  getHoldings() {
    const items = [];

    const stocks = this._getStocksMap();
    const tickers = Object.keys(stocks).sort((a, b) => a.localeCompare(b));
    tickers.forEach((ticker) => {
      const currentStock = stocks[ticker];
      const upsideDownside = (
        typeof currentStock.lastPrice === 'number' &&
        typeof currentStock.targetPrice === 'number' &&
        currentStock.lastPrice
      )
        ? ((currentStock.targetPrice - currentStock.lastPrice) / currentStock.lastPrice) * 100
        : null;

      items.push({
        ticker: ticker,
        currentPrice: typeof currentStock.lastPrice === 'number' ? currentStock.lastPrice : null,
        currentValue: currentStock.currentValue,
        change: Math.round(((currentStock.lastPrice - currentStock.avgPurchasePrice) / currentStock.avgPurchasePrice) * 100),
        targetPrice: typeof currentStock.targetPrice === 'number' ? currentStock.targetPrice : null,
        upsideDownside
      });
    });

    return {
      columns: [
        'Stock Ticker',
        'Current Value',
        'Gain / Loss',
        'Current Price',
        'Target Price',
        'Upside / Downside'
      ],
      items
    };
  }

  _getAssetClass(ticker, stock) {
    if (this._isCashHolding(ticker, stock)) {
      return 'Cash';
    }

    const sector = stock && stock.sector ? stock.sector.toLowerCase() : '';
    if (sector === 'etf' && this._isBondEtf(ticker)) {
      return 'Fixed Income';
    }

    return 'Equities';
  }

  _isCashHolding(ticker, stock) {
    const cashTickers = new Set(['VMFXX', 'SPAXX', 'SWVXX', 'FDRXX', 'SPRXX']);
    if (cashTickers.has(ticker)) {
      return true;
    }

    return Boolean(
      stock &&
      !stock.sector &&
      typeof stock.lastPrice === 'number' &&
      stock.lastPrice <= 1.01
    );
  }

  _isBondEtf(ticker) {
    const bondEtfs = new Set([
      'AGG', 'BIL', 'BND', 'BNDX', 'BSV', 'EMB', 'GOVT', 'HYG', 'IEF', 'JNK',
      'LQD', 'MUB', 'SCHZ', 'SHV', 'SHY', 'TIP', 'TLT', 'VCIT', 'VGIT', 'VGSH',
      'BAB', 'PCY'
    ]);
    return bondEtfs.has(ticker);
  }

  _getStocksMap() {
    const { transactions } = this;
    if (transactions.length < 1) return [];

    const stocks = {};
    transactions.forEach((txn) => {
      const alreadyAdded = stocks[txn.stockTicker];
      if (alreadyAdded) {
        stocks[txn.stockTicker] = {
          avgPurchasePrice: ((alreadyAdded.avgPurchasePrice * alreadyAdded.totalShares) + (txn.purchasePrice * txn.numberOfShares)) /
            (alreadyAdded.totalShares + txn.numberOfShares),
          totalShares: alreadyAdded.totalShares + txn.numberOfShares,
          currentValue: txn.getStock().lastPrice * (alreadyAdded.totalShares + txn.numberOfShares),
          lastPrice: txn.getStock().lastPrice,
          targetPrice: txn.getStock().targetPrice
        };
      } else {
        stocks[txn.stockTicker] = {
          avgPurchasePrice: txn.purchasePrice,
          totalShares: txn.numberOfShares,
          currentValue: txn.getStock().lastPrice * txn.numberOfShares,
          lastPrice: txn.getStock().lastPrice,
          targetPrice: txn.getStock().targetPrice
        };
      }
    });

    return stocks;
  }

  _getPortfolioValueForStocksMap(stocks) {
    const entries = Object.values(stocks);
    if (entries.length < 1) {
      return 0;
    }

    return entries.reduce((sum, stock) => {
      const { currentValue } = stock;
      if (typeof currentValue === 'number' && Number.isFinite(currentValue)) {
        return sum + currentValue;
      }
      return sum;
    }, 0);
  }

  _getLastChartValue() {
    const values = this.chartData?.values;
    if (!Array.isArray(values) || values.length < 1) {
      return null;
    }

    for (let index = values.length - 1; index >= 0; index -= 1) {
      const numericValue = Number(values[index]);
      if (Number.isFinite(numericValue)) {
        return numericValue;
      }
    }

    return null;
  }

  _getLastTwoChartValues() {
    const values = this.chartData?.values;
    if (!Array.isArray(values)) {
      return null;
    }

    let previous = null;
    let current = null;
    values.forEach((value) => {
      const numericValue = Number(value);
      if (Number.isFinite(numericValue)) {
        previous = current;
        current = numericValue;
      }
    });

    if (previous == null || current == null) {
      return null;
    }

    return { previous, current };
  }

  _getAlignedPeriodReturns() {
    if (!this.chartData?.dates?.length || !this.chartData?.values?.length) {
      return null;
    }

    const benchmarkSeries = this._getUsBenchmarkSeries();
    if (!benchmarkSeries) {
      return null;
    }

    const { dates: portfolioDates, values: portfolioValues } = this.chartData;
    const { dates: benchmarkDates, data: benchmarkCumulativePct } = benchmarkSeries;
    const portfolioDateToIndex = new Map(portfolioDates.map((date, index) => [date, index]));
    const overlappingDates = benchmarkDates.filter((date) => portfolioDateToIndex.has(date));

    if (overlappingDates.length < 2) {
      return null;
    }

    const startDate = overlappingDates[0];
    const endDate = overlappingDates[overlappingDates.length - 1];
    const portfolioStartIndex = portfolioDateToIndex.get(startDate);
    const portfolioEndIndex = portfolioDateToIndex.get(endDate);
    const benchmarkStartIndex = benchmarkDates.indexOf(startDate);
    const benchmarkEndIndex = benchmarkDates.indexOf(endDate);
    const portfolioStartValue = portfolioValues[portfolioStartIndex];
    const portfolioEndValue = portfolioValues[portfolioEndIndex];

    if (!portfolioStartValue || !portfolioEndValue) {
      return null;
    }

    const portfolioReturn = (portfolioEndValue / portfolioStartValue) - 1;
    const marketReturn = (
      (1 + benchmarkCumulativePct[benchmarkEndIndex] / 100) /
      (1 + benchmarkCumulativePct[benchmarkStartIndex] / 100)
    ) - 1;

    return {
      portfolioReturn,
      marketReturn,
      tradingDays: overlappingDates.length - 1
    };
  }

  _getUsBenchmarkSeries() {
    if (!this.benchmarkData?.dates?.length || !this.benchmarkData?.prices?.length) {
      return null;
    }

    const usSeries = this.benchmarkData.prices.find((series) => series.name === 'United States');
    if (!usSeries?.data?.length) {
      return null;
    }

    return {
      dates: this.benchmarkData.dates,
      data: usSeries.data
    };
  }
}

export default Portfolio;
